import React from 'react';
import { Box, Typography, Button, TextField, Stack, Paper, Divider, CircularProgress } from '@mui/material';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LanguageIcon from '@mui/icons-material/Language';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import { urlImg } from '../common/helpers';

const SummaryItem = ({ icon: Icon, label, value }) => (
  <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
    <Box sx={{ 
      color: '#0E2465', 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center',
      '& svg': { fontSize: 20 }
    }}>
      <Icon />
    </Box>
    <Box>
      <Typography variant="caption" color="#737791" sx={{ display: 'block', fontWeight: 500, mb: -0.5 }}>
        {label}
      </Typography>
      <Typography variant="body2" fontWeight="bold" color="#0E2465">
        {value}
      </Typography>
    </Box>
  </Stack>
);

const BookingSummary = ({
  service, profile, ownerUser,
  selectedDate, selectedTime,
  selectedLanguage, selectedModality,
  paymentMethod,
  quote = null,
  onConfirm,
  isConfirming = false,
  promoCodigo = '', setPromoCodigo = () => {},
  promoAplicado = null, promoError = '', promoLoading = false,
  onAplicarPromo = () => {}, onQuitarPromo = () => {}
}) => {
  const { nombre, costoServicio, img, duracionEstimada, unidadDuracion } = service;

  const finalCost = quote ? quote.costo : costoServicio;
  const subtotal = Number(finalCost) || 0;
  // Descuento del código según su tipo (porcentaje / monto fijo), respetando la compra mínima.
  const promoMin = promoAplicado?.montoMinimo || 0;
  const promoMinNoCumple = !!promoAplicado && subtotal < promoMin;
  const descuentoMonto = (!promoAplicado || promoMinNoCumple) ? 0 : Math.min(
    promoAplicado.tipo === 'monto_fijo'
      ? (promoAplicado.montoFijo || 0)
      : Math.round(subtotal * (promoAplicado.descuentoPct || 0)) / 100,
    subtotal
  );
  const total = Math.round((subtotal - descuentoMonto) * 100) / 100;
  const promoLabel = promoAplicado
    ? (promoAplicado.tipo === 'monto_fijo'
        ? `-$${Number(promoAplicado.montoFijo || 0).toFixed(2)}`
        : `-${promoAplicado.descuentoPct || 0}%`)
    : '';
  const finalName = quote ? `Cotización: ${quote.concepto}` : nombre;
  const finalDuration = quote ? '1' : duracionEstimada;
  const finalUnit = quote ? (quote.unidad || 'proyecto') : (unidadDuracion || 'horas');
  
  const serviceImg = img && img.length > 0 
    ? `${urlImg}/servicios/${service._id}/${img[0].nombre}` 
    : '/search.png';

  const authorName = ownerUser ? `${ownerUser.name} ${ownerUser.apellido}` : (profile?.nombre || 'Proveedor');

  const formatDate = (date) => {
    if (!date) return '';
    const d = new Date(date);
    const months = [
      "enero", "febrero", "marzo", "abril", "mayo", "junio",
      "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre"
    ];
    return `${d.getDate()} de ${months[d.getMonth()]}`;
  };

  const getPaymentLabel = (method) => {
    switch (method) {
      case 'visa': return 'VISA •• 4242';
      case 'mastercard': return 'MASTERCARD •• 5555';
      case 'paypal': return 'PayPal';
      case 'google': return 'Google Pay';
      case 'new': return 'Nueva tarjeta';
      default: return 'Tarjeta guardada';
    }
  };

  return (
    <Paper 
      elevation={0} 
      sx={{ 
        p: 4, 
        borderRadius: '24px', 
        bgcolor: '#EFF6FF', 
        position: 'sticky', 
        top: 100,
        border: '1px solid #E0E7FF'
      }}
    >
      <Typography variant="h5" fontWeight="900" color="#0E2465" sx={{ mb: 4 }}>
        Resumen
      </Typography>

      <Box sx={{ bgcolor: 'transparent', mb: 4 }}>
        <Box 
          component="img"
          src={serviceImg}
          sx={{ width: '100%', height: 180, objectFit: 'cover', borderRadius: '16px', mb: 2 }}
        />
        <Typography variant="subtitle1" fontWeight="bold" color="#0E2465" sx={{ mb: 0.5, lineHeight: 1.2 }}>
          {finalName}
        </Typography>
        <Typography variant="body2" color="#737791" sx={{ fontWeight: 500 }}>
          {quote ? 'Proyecto personalizado' : `${finalDuration} ${finalUnit}`} • {authorName}
        </Typography>
      </Box>

      <Divider sx={{ mb: 4, borderColor: '#E2E8F0', opacity: 0.6 }} />

      <Box sx={{ mb: 4 }}>
        <SummaryItem icon={CalendarTodayIcon} label="Fecha" value={formatDate(selectedDate)} />
        <SummaryItem icon={AccessTimeIcon} label="Hora" value={selectedTime ? `${selectedTime} hrs` : 'Por seleccionar'} />
        <SummaryItem icon={LanguageIcon} label="Idioma" value={selectedLanguage} />
        <SummaryItem icon={LocationOnIcon} label="Modalidad" value={selectedModality} />
        <SummaryItem icon={CreditCardIcon} label="Pago" value={getPaymentLabel(paymentMethod)} />
      </Box>

      <Divider sx={{ mb: 4, borderColor: '#E2E8F0', opacity: 0.6 }} />

      <Box sx={{ mb: 4 }}>
        <Typography variant="caption" fontWeight="bold" color="#737791" sx={{ mb: 1.5, display: 'block' }}>
          Código promocional
        </Typography>
        {promoAplicado ? (
          <Box sx={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            bgcolor: '#D6F3E8', border: '1px solid #59C39A', borderRadius: '12px', px: 2, py: 1.2
          }}>
            <Box>
              <Typography variant="body2" fontWeight={900} color="#1B7A5A">{promoAplicado.codigo}</Typography>
              <Typography variant="caption" color={promoMinNoCumple ? '#B23B33' : '#1B7A5A'}>
                {promoMinNoCumple ? `Requiere compra mínima de $${Number(promoMin).toFixed(2)}` : `${promoLabel} aplicado`}
              </Typography>
            </Box>
            <Button onClick={onQuitarPromo} size="small" sx={{ textTransform: 'none', color: '#1B7A5A', fontWeight: 700 }}>
              Quitar
            </Button>
          </Box>
        ) : (
          <>
            <Stack direction="row" spacing={1}>
              <TextField
                placeholder="Ej. ONESERVICE20"
                size="small"
                fullWidth
                value={promoCodigo}
                onChange={(e) => setPromoCodigo(e.target.value.toUpperCase())}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); onAplicarPromo(); } }}
                error={!!promoError}
                sx={{
                  bgcolor: 'white',
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '12px',
                    '& fieldset': { borderColor: '#E2E8F0' }
                  }
                }}
              />
              <Button
                variant="contained"
                onClick={onAplicarPromo}
                disabled={promoLoading || !promoCodigo.trim()}
                sx={{
                  bgcolor: '#0E2465',
                  color: 'white',
                  textTransform: 'none',
                  fontWeight: '900',
                  borderRadius: '12px',
                  px: 3,
                  boxShadow: 'none',
                  '&:hover': { bgcolor: '#0a1a4a', boxShadow: 'none' },
                  '&.Mui-disabled': { bgcolor: '#CBD5E1', color: 'white' }
                }}
              >
                {promoLoading ? <CircularProgress size={18} sx={{ color: 'white' }} /> : 'Aplicar'}
              </Button>
            </Stack>
            {promoError && (
              <Typography variant="caption" color="#B23B33" sx={{ mt: 0.75, display: 'block', fontWeight: 600 }}>
                {promoError}
              </Typography>
            )}
          </>
        )}
      </Box>

      <Stack spacing={1} sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="body2" color="#737791" fontWeight={500}>Subtotal</Typography>
          <Typography variant="body2" fontWeight="bold" color="#0E2465">${subtotal.toFixed(2)}</Typography>
        </Box>
        {descuentoMonto > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2" color="#1B7A5A" fontWeight={600}>
              Descuento ({promoAplicado.codigo} {promoLabel})
            </Typography>
            <Typography variant="body2" fontWeight="bold" color="#1B7A5A">-${descuentoMonto.toFixed(2)}</Typography>
          </Box>
        )}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
          <Typography variant="h5" fontWeight="900" color="#0E2465">Total</Typography>
          <Typography variant="h5" fontWeight="900" color="#0E2465">${total.toFixed(2)}</Typography>
        </Box>
      </Stack>

      <Button
        variant="contained"
        fullWidth
        onClick={onConfirm}
        disabled={isConfirming}
        sx={{
          bgcolor: '#0E2465',
          color: 'white',
          py: 2,
          textTransform: 'none',
          fontWeight: '900',
          borderRadius: '14px',
          fontSize: '16px',
          mb: 3,
          boxShadow: '0px 8px 16px rgba(14, 36, 101, 0.2)',
          '&:hover': { bgcolor: '#0a1a4a', boxShadow: '0px 12px 20px rgba(14, 36, 101, 0.3)' },
          '&.Mui-disabled': { bgcolor: '#94A3B8', color: 'white' }
        }}
      >
        {isConfirming ? <CircularProgress size={22} sx={{ color: 'white' }} /> : 'Confirmar reserva'}
      </Button>

      <Typography variant="caption" color="#737791" textAlign="center" display="block" sx={{ px: 2, lineHeight: 1.4, fontSize: '11px' }}>
        Al confirmar aceptas los términos y condiciones del servicio
      </Typography>
    </Paper>
  );
};

export default BookingSummary;
